import { useState } from "react";
import { useKV } from "@/hooks/use-kv-adapter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Candidate, ApplicationStage } from "@/types/recruitment";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";

const STAGES: ApplicationStage[] = ['Applied', 'Screening', 'Interview', 'Offer', 'Hired', 'Rejected'];

export function CandidatePipeline() {
  const [candidates, setCandidates] = useKV<any[]>("recruitment-candidates", [
    { id: 'c1', firstName: 'Alice', lastName: 'Johnson', stage: 'Applied', role: 'Frontend Engineer' },
    { id: 'c2', firstName: 'Bob', lastName: 'Smith', stage: 'Screening', role: 'Product Manager' },
    { id: 'c3', firstName: 'Charlie', lastName: 'Brown', stage: 'Interview', role: 'Frontend Engineer' },
  ]);

  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [newStage, setNewStage] = useState<ApplicationStage>('Applied');

  const onDragEnd = (result: any) => {
    if (!result.destination) return;

    const { draggableId, destination } = result;
    const destStage = destination.droppableId as ApplicationStage;

    const updated = candidates.map(c => 
      c.id === draggableId ? { ...c, stage: destStage } : c
    );
    setCandidates(updated);
    toast.success(`Moved candidate to ${destStage}`);
  };

  const handleMoveStage = () => {
    if (!selectedCandidate) return;
    const updated = candidates.map(c => 
      c.id === selectedCandidate.id ? { ...c, stage: newStage } : c
    );
    setCandidates(updated);
    setIsMoveDialogOpen(false);
    toast.success(`Moved ${selectedCandidate.firstName} to ${newStage}`);
  };

  return (
    <div className="h-[calc(100vh-200px)] overflow-x-auto pb-4">
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 min-w-max h-full">
          {STAGES.map(stage => (
            <Droppable key={stage} droppableId={stage}>
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="w-80 bg-slate-100 dark:bg-slate-900/50 rounded-lg p-4 flex flex-col h-full"
                >
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="font-semibold text-sm uppercase text-slate-500">{stage}</h4>
                    <Badge variant="secondary">{candidates.filter(c => c.stage === stage).length}</Badge>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto space-y-3">
                    {candidates.filter(c => c.stage === stage).map((candidate, index) => (
                      <Draggable key={candidate.id} draggableId={candidate.id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className="bg-white dark:bg-slate-800 p-4 rounded border shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow"
                            onClick={() => { setSelectedCandidate(candidate); setNewStage(candidate.stage); setIsMoveDialogOpen(true); }}
                          >
                            <div className="flex items-center gap-3 mb-2">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback>{candidate.firstName[0]}</AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{candidate.firstName} {candidate.lastName}</div>
                                <div className="text-xs text-slate-500">{candidate.role}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      <Dialog open={isMoveDialogOpen} onOpenChange={setIsMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Candidate</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">Select Stage</label>
            <Select value={newStage} onValueChange={(v) => setNewStage(v as ApplicationStage)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMoveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleMoveStage}>Move</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
